import { FC, ReactElement, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Box, Typography, styled, useTheme } from '@mui/material'
import NumberFormat from 'react-number-format'
import * as Yup from 'yup'
import { Button, FloatingInput, RadioButton, RadioGroup } from '@icanbwell/ui-toolkit'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { parsePhoneNumber } from 'libphonenumber-js'
import { useEnableMfaMutation, useUserLoginMutation } from '@client-hub/store/api/crs/react'
import type { EnableMfaPayload } from '@client-hub/store/api/crs/types'
import { MFA_METHOD } from '@client-hub/shared/constants'
import ClientLogo from 'components/ClientLogo'
import { HEADER_HEIGHT } from '@client-hub/shared/constants/styles'
import { useTranslation } from 'react-i18next'
import { get } from 'lodash'
import ROUTES from '@client-hub/shared/constants/routes'
import { useNotifications } from '@client-hub/shared/react/hooks/notifications'
import { CRS_LOGIN_MUTATION_CACHE_KEY } from 'constants/auth'

const Wrapper = styled(Box)({
  minHeight: `calc(100vh - ${HEADER_HEIGHT})`,
  maxWidth: '420px',
  paddingTop: '3rem',
  display: 'flex',
  flexDirection: 'column',
  margin: '0 auto',
})

const RadioButtonStyled = styled(RadioButton)(({ theme }) => ({
  '& + .MuiFormControlLabel-label': {
    color: theme.palette.grey['700'],
    fontSize: '1.2rem',
  },
}))

const PHONE_PREFIX = '+1'
const addPhonePrefix = (v: string) => `${PHONE_PREFIX}${v}`

const MfaSchema = () => {
  const { t } = useTranslation()

  return Yup.object({
    two_factor_auth: Yup.mixed<'0' | '1' | '2'>().oneOf(['0', '1', '2']).default('0').required(),
    phone_number: Yup.string()
      .default('')
      .when('two_factor_auth', {
        is: MFA_METHOD.SMS.toString(),
        then: Yup.string()
          .test('test-length', (val) => !(!val || val.length < 10))
          .test(
            'test-area',
            t('forms.mfaSetup.fields.phone.validation.areaCode'),
            (val) => !!val && val.length === 10 && parsePhoneNumber(val, 'US').isValid(),
          )
          .required(),
      }),
  }).required()
}

type MfaData = Yup.TypeOf<ReturnType<typeof MfaSchema>>

export type MfaEnableContentType = {
  showLogo?: boolean
}

type Props = {
  content?: MfaEnableContentType
  onSuccess: (state: Record<string, unknown>) => void
  uid?: string
}

const MfaEnable: FC<Props> = ({ content = {}, onSuccess, uid }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const notify = useNotifications()
  const navigate = useNavigate()
  const location = useLocation()
  const [, { reset }] = useUserLoginMutation({ fixedCacheKey: CRS_LOGIN_MUTATION_CACHE_KEY })
  const [enableMfa, { isSuccess: isSuccessEnableMfa, data: enableMfaData, error: enableMfaError }] =
    useEnableMfaMutation()
  const {
    handleSubmit,
    control,
    watch,
    formState: { isValid, errors },
    getValues,
  } = useForm<MfaData>({
    resolver: yupResolver(MfaSchema()),
    mode: 'onChange',
    defaultValues: MfaSchema().cast({}),
  })
  const two_factor_auth = watch('two_factor_auth')
  const areaCodeError = errors.phone_number?.type === 'test-area' && errors.phone_number?.message

  const handleBack = () => navigate(-1)

  useEffect(() => {
    if (isSuccessEnableMfa && enableMfaData) {
      const { uid = '', totp_url = '', sms_status } = enableMfaData
      const state = {
        uid,
        isAllowedMfa: true,
        two_factor_auth: +two_factor_auth,
        ...(+two_factor_auth === MFA_METHOD.AUTHENTICATOR_APP && { totp_url }),
        ...(+two_factor_auth === MFA_METHOD.SMS && { sms_status, phone_number: getValues('phone_number') }),
      }
      onSuccess(state)
    }
  }, [enableMfaData, getValues, isSuccessEnableMfa, location, navigate, onSuccess, two_factor_auth])

  useEffect(() => {
    if (enableMfaError && get(enableMfaError, 'status') === 401) {
      notify({ message: get(enableMfaError, 'data.detail'), variant: 'error' })
      reset()
      navigate(ROUTES.LOGIN.BASE)
    }
  }, [enableMfaError, navigate, notify, location])

  const handleForm = ({ two_factor_auth, phone_number }: MfaData) => {
    const phoneWithPrefix = `${PHONE_PREFIX}${phone_number}`
    const requestData: EnableMfaPayload = {
      two_factor_auth: +two_factor_auth,
      ...(+two_factor_auth === MFA_METHOD.SMS && { phone_number: phoneWithPrefix }),
      uid,
    }
    enableMfa(requestData)
  }

  return (
    <Wrapper>
      <Box
        component="form"
        autoComplete="off"
        display="flex"
        flexDirection="column"
        onSubmit={handleSubmit(handleForm)}
      >
        {content.showLogo && <ClientLogo variant="dark" />}
        <Typography
          component="h1"
          sx={{
            color: 'common.primaryBase',
            fontSize: '1.7rem',
            lineHeight: '2.1rem',
            marginTop: '2rem',
          }}
        >
          {t('routes.mfaSetup.title')}
        </Typography>
        <Typography component="p">{t('routes.mfaSetup.subtitle')}</Typography>
        <Controller
          name="two_factor_auth"
          control={control}
          render={({ field: { onChange, value, name } }) => (
            <RadioGroup name={name} value={value} onChange={onChange} style={{ width: '100%' }} sx={{
                '& > .MuiFormControlLabel-root:nth-of-type(2)': {
                  position: 'relative',
                  marginBottom: '10px',
                  '& .MuiFormControlLabel-label::after': {
                    background: theme.palette.common.accentBase, 
                    color: theme.palette.common.white, 
                    content: `'${t('routes.mfaSetup.recommended')}'`, 
                    position: 'absolute', 
                    right: '30px', 
                    top: '-5px',
                    padding: '5px 10px',
                    borderRadius: '10px'
                  }
                }
            }}>
              <RadioButtonStyled
                label={t('forms.mfaSetup.fields.sms.label')}
                value={MFA_METHOD.SMS}
                style={{ fontSize: '1.4rem' }}
              />
              <Typography component="p" sx={{ marginLeft: '23px', marginTop: '-17px' }}>
                {t('routes.mfaSetup.weWillSendSms')}
              </Typography>
              {+two_factor_auth === MFA_METHOD.SMS ? (
                <Controller
                  name="phone_number"
                  control={control}
                  render={({ field: { onChange: onPhoneChange, ...props } }) => (
                    <NumberFormat
                      customInput={FloatingInput}
                      allowEmptyFormatting
                      mask="_"
                      label={t('forms.mfaSetup.fields.phone.label')}
                      format={addPhonePrefix('(###) ###-####')}
                      error={!!areaCodeError}
                      helperText={areaCodeError}
                      onValueChange={({ value }) => onPhoneChange(value)}
                      {...props}
                    />
                  )}
                />
              ) : (
                (null as unknown as ReactElement)
              )}
              <RadioButtonStyled
                label={t('forms.mfaSetup.fields.app.label')}
                value={MFA_METHOD.AUTHENTICATOR_APP}
                style={{ fontSize: '1.4rem' }}
              />
              <Typography component="p" sx={{ marginLeft: '23px', marginTop: '-17px' }}>
                {t('routes.mfaSetup.weRecommend')}
              </Typography>
            </RadioGroup>
          )}
        />
        <Box display="flex" flexDirection="column" alignItems="center" marginTop="1rem">
          <Button type="submit" disabled={!isValid || !+two_factor_auth}>
            {t('routes.mfaSetup.continue')}
          </Button>
          <Button onClick={handleBack} variant="text" sx={{ marginTop: '1rem' }}>
            {t('routes.mfaSetup.back')}
          </Button>
        </Box>
      </Box>
    </Wrapper>
  )
}

export default MfaEnable
